import IndigoApi from 'communication/IndigoApi';
import { ContactSupportRequestState } from 'communication/types/ContactSupportRequest';
import RequestProgressService from 'requestProgress/RequestProgressService';
import { Store, AnyAction } from 'redux';
import State from 'store/State';
import { mapStateToSupport } from './contactSupportMapper';
import { PortfolioService } from 'portfolio/PortfolioService';
import { setGlobalWindowVariable } from 'utils/globalWindowVariables';
import { LogMessagesStoreService } from 'logging/store/LogMessagesStoreService';
import { selectMachineId } from 'app/store/general/selectors';
import IndigoServiceClient from 'communication/IndigoServiceClient';
import { EmbeddedApiService } from 'embedded/api/EmbeddedApiService';
import { WidgetStreamingService } from 'widgets/streaming/WidgetStreamingService';
import { MemoryMonitoringService } from 'performance/MemoryMonitoringService';

interface ContactSupportServiceOptions {
    indigoApi: IndigoApi;
    indigoServiceClient: IndigoServiceClient;
    requestProgressService: RequestProgressService;
    portfolioService: PortfolioService;
    logMessagesStoreService: LogMessagesStoreService;
    embeddedApiService: EmbeddedApiService;
    memoryMonitoring: MemoryMonitoringService;
    widgetStreamingService: WidgetStreamingService;
}

export default class ContactSupportService {
    private readonly indigoApi: IndigoApi;
    private requestProgressService: RequestProgressService;
    private readonly portfolioService: PortfolioService;
    private readonly indigoServiceClient: IndigoServiceClient;
    private readonly logMessagesStoreService: LogMessagesStoreService;
    private readonly embeddedApiService: EmbeddedApiService;
    private readonly widgetStreamingService: WidgetStreamingService;
    private readonly memoryMonitoring: MemoryMonitoringService;

    public store: Store<State, AnyAction> | undefined;

    public constructor(options: ContactSupportServiceOptions) {
        this.indigoApi = options.indigoApi;
        this.requestProgressService = options.requestProgressService;
        this.portfolioService = options.portfolioService;
        this.logMessagesStoreService = options.logMessagesStoreService;
        this.indigoServiceClient = options.indigoServiceClient;
        this.embeddedApiService = options.embeddedApiService;
        this.widgetStreamingService = options.widgetStreamingService;
        this.memoryMonitoring = options.memoryMonitoring;

        setGlobalWindowVariable({ logState: () => this.logState() });
    }

    public async sendSupportRequest(
        enquiry: string,
        name?: string,
        screenshot?: string,
        forDevTeam?: boolean
    ) {
        const state = this.getSupportState(name, screenshot);
        const machineId = this.getMachineId();

        const apiCall = () =>
            this.indigoApi.postContactSupportRequest(
                enquiry,
                state,
                machineId,
                forDevTeam
            );
        return await this.requestProgressService.invokeAndMonitorRequest(
            'sendSupportRequest',
            apiCall
        );
    }

    public logState() {
        return this.getSupportState();
    }

    public getSupportState(
        name?: string,
        screenshot?: string
    ): ContactSupportRequestState {
        return mapStateToSupport(
            this.getState(),
            name,
            screenshot,
            this.portfolioService,
            this.logMessagesStoreService,
            this.indigoApi,
            this.indigoServiceClient,
            this.embeddedApiService,
            this.widgetStreamingService,
            this.memoryMonitoring
        );
    }

    public getMachineId(): string | undefined {
        return selectMachineId(this.getState());
    }

    private getState() {
        if (!this.store) {
            throw new Error('store must be set');
        }

        return this.store.getState();
    }
}
